import type { ComputedRef } from 'vue';

import type { AuthUserFieldsFragment } from '~/graphql/generated/graphql';

import { serverFlagKey, useServerFlagRef } from '~/composables/useServerFlag';
import { UNIFIED_ROUTE_INDEX } from '~/constants/routes/unified';
import { useConfigStore } from '~/stores/config';
import MockProvider from '~/utils/featureFlags/MockProvider';
import { serverFlagAnswer } from '~/utils/featureFlags/serverFlagAnswer';

/**
 * Whether the new information architecture applies to this visitor, and as whom: not at all, as a
 * guest (who reads Learn and Explore before signing in), or as a signed-in student (who gets the
 * signed-in chrome).
 */
export const IaAudience = {
  Off: 'off',
  Guest: 'guest',
  Student: 'student',
} as const;
export type IaAudiences = typeof IaAudience[keyof typeof IaAudience];

/** Who a page is for once the IA applies: any visitor it applies to, or a signed-in student only. */
export const IaPageAudience = {
  All: 'all',
  Student: 'student',
} as const;
export type IaPageAudiences = typeof IaPageAudience[keyof typeof IaPageAudience];

/**
 * A page's place in the new information architecture, as its `ia` page meta. The `05a.ia`
 * middleware reads it, with the visitor's audience, to pick the redirect.
 */
export interface IaPageMeta {
  /** Who the page is for once the IA applies: any visitor it applies to, or a signed-in student only. */
  audience: IaPageAudiences;
  /** Where a visitor the page is not for goes. Left out, they get the page as it is. */
  off?: string;
  /**
   * For a public page a signed-in student has in the coach shell: the shell's page, which they get
   * instead. `:name` segments are filled from the route's params (`/coach/badges/:slug`), and the
   * query is carried.
   */
  on?: string;
  /**
   * Full width in the public layout while the IA applies, for a page whose IA version sets its own
   * width.
   */
  fullWidth?: boolean;
}

export interface IaVisitor {
  flagOn: boolean;
  schoolsCluster: boolean;
  /** Signed in with the role the unified route guard asks for, so the student places let them in. */
  student: boolean;
  ghost: boolean;
}

const IA_FLAG = 'ia-enabled';

/**
 * The one rule every IA surface reads. The schools product keeps its own chrome whatever the flag
 * says, and a ghost (a guest part-way through a card) is still a guest: they have no places of
 * their own to move between yet.
 */
export const iaAudienceFor = ({ flagOn, schoolsCluster, student, ghost }: IaVisitor): IaAudiences => {
  if (!flagOn || schoolsCluster) {
    return IaAudience.Off;
  }

  return student && !ghost ? IaAudience.Student : IaAudience.Guest;
};

/**
 * The unified route guard's own rule, so a teacher or a content admin signed in on the consumer
 * site reads as a guest rather than getting a bar of places that would send them back to `/`.
 */
const isStudent = (user: AuthUserFieldsFragment | null | undefined): boolean =>
  user ? usePlatformAppConfig(UNIFIED_ROUTE_INDEX).userHasRequiredRole(user) : false;

/**
 * The audience for route middleware. It takes the flag the way `serverFlagAnswer` does, so a route
 * decided in the browser agrees with the page the server rendered.
 */
export const iaAudience = async (): Promise<IaAudiences> => {
  const { user, isGhostStudent } = useAuth();
  const schoolsCluster = useConfigStore().isSchoolsCluster;
  const flagOn = await serverFlagAnswer(IA_FLAG);

  return iaAudienceFor({ flagOn, schoolsCluster, student: isStudent(user.value), ghost: Boolean(isGhostStudent.value) });
};

/**
 * The audience for setup code. The flag goes through `useServerFlagRef`, so the server's answer is
 * in the payload for hydration and for the middleware of the next navigation. Signing in or out
 * asks it again (`reaskIaFlag`), and the audience follows the new answer, so a layout that stays
 * mounted through the sign-in moves with it. Call it before the caller's first await, as
 * `useServerFlagRef` requires.
 */
export const useIaAudience = async (): Promise<ComputedRef<IaAudiences>> => {
  const { user, isGhostStudent } = useAuth();
  const schoolsCluster = useConfigStore().isSchoolsCluster;
  const flagOn = await useServerFlagRef(IA_FLAG);

  return computed(() => iaAudienceFor({ flagOn: flagOn.value, schoolsCluster, student: isStudent(user.value), ghost: Boolean(isGhostStudent.value) }));
};

/**
 * Whether the IA applies, for code that cannot await: the same rule, on the flag answer the page has
 * already been given (by `useIaAudience` or the middleware). A flag never asked reads as off.
 */
export const useIaApplies = (): ComputedRef<boolean> => {
  const { user, isGhostStudent } = useAuth();
  const schoolsCluster = useConfigStore().isSchoolsCluster;
  const { data: flagOn } = useNuxtData<boolean>(serverFlagKey(IA_FLAG));

  return computed(() => iaAudienceFor({ flagOn: flagOn.value === true, schoolsCluster, student: isStudent(user.value), ghost: Boolean(isGhostStudent.value) }) !== IaAudience.Off);
};

/**
 * Asks the flag again for the visitor who has just signed in or out. The flag can target an
 * account or a state, and the answer the guest's page was given would otherwise last until a
 * reload: the next page reuses it, and so does the middleware.
 *
 * A browser whose Unleash client could not start falls back to the static bootstrap, which has the
 * flag off. Asking that provider would swap the server's answer for the bootstrap's, so it keeps the
 * server's answer instead.
 *
 * Only the payload's copy is dropped before the refresh, so middleware with no page holding the
 * flag asks the provider. A page that holds it keeps its answer on screen until the new one lands,
 * rather than reading the flag as off in between.
 */
export const reaskIaFlag = async (): Promise<void> => {
  const nuxtApp = useNuxtApp();
  const browserOnBootstrap = useRuntimeConfig().public.featureFlagsServiceEnabled
    && nuxtApp.$featureFlagsProvider instanceof MockProvider;
  if (browserOnBootstrap) {
    return;
  }

  delete nuxtApp.payload.data[serverFlagKey(IA_FLAG)];
  await refreshNuxtData(serverFlagKey(IA_FLAG));
};
